import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { useShipOrder } from '~/api';
import { extractGraphQLErrorMessages } from '~/utils';
import { Modal } from '~/components/overlays';
import { ShippingInfo } from '~/pages/order-view/sections/ShippingInfo';

/**
 * Shared ship-order modal + mutation logic, used both by the regular seller
 * "Ship Order" call to action and by the trader Sourcing panel's
 * "Ship from my inventory" option, so both trigger the exact same flow.
 */
export function useShipOrderModal(orderId: string) {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const { mutate: shipOrder, isPending: isShippingOrder } = useShipOrder();
  const [isShipModalOpen, setIsShipModalOpen] = useState(false);
  const isShipInFlight = useRef(false);

  const handleShipOrder = (
    shipDate: Date,
    shipStartDate: Date,
    shipEndDate: Date | null
  ) => {
    if (isShipInFlight.current) return;
    isShipInFlight.current = true;
    shipOrder(
      {
        orderId,
        deliveryStartDate: shipStartDate.toISOString(),
        shippedDate: shipDate.toISOString(),
        deliveryEndDate: shipEndDate ? shipEndDate.toISOString() : undefined,
      },
      {
        onSuccess: () => {
          setIsShipModalOpen(false);
          toast.success(t('order.ship_success'));
          navigate(`/orders/view/${orderId}`);
        },
        onError: (error) => {
          toast.error(
            t('order.ship_error', {
              message: extractGraphQLErrorMessages(error).join(', '),
            })
          );
        },
        onSettled: () => {
          isShipInFlight.current = false;
        },
      }
    );
  };

  const shipModal = (
    <Modal isOpen={isShipModalOpen} onClose={() => setIsShipModalOpen(false)}>
      <ShippingInfo
        onConfirm={handleShipOrder}
        onCancel={() => setIsShipModalOpen(false)}
        isShipping={isShippingOrder}
      />
    </Modal>
  );

  return {
    shipModal,
    openShipModal: () => setIsShipModalOpen(true),
    isShippingOrder,
  };
}
